import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { C, fmt, audit, getComprobanteUrl } from '../constants';
import { Btn, Card, Toast } from '../components/Atoms';
import { NavbarApp } from './MisTandas';

// ─── PANEL ADMIN ───────────────────────────────────────────
function PanelAdmin({ usuario, onMenu, onHome }) {
  const [tandas, setTandas] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vistaAdmin, setVistaAdmin] = useState('pendientes'); // pendientes | tandas
  const [toast, setToast] = useState({ msg: '', type: '' });
  const showToast = (msg, type = 'info') => { setToast({ msg, type }); setTimeout(() => setToast({ msg: '', type: '' }), 4000); };

  useEffect(() => { cargar(); }, []); // eslint-disable-line

  const [errorCarga, setErrorCarga] = useState('');

  const cargar = async () => {
    setLoading(true);
    setErrorCarga('');
    try {
      // Cargar todo por separado (evita nested joins que fallan con RLS)
      const [pagosRes, tandasRes, partsRes] = await Promise.all([
        supabase.from('pagos').select('*').eq('pagado', false).not('comprobante_url', 'is', null).order('created_at', { ascending: false }),
        supabase.from('tandas').select('*').order('created_at', { ascending: false }),
        supabase.from('participantes').select('id, turno, usuario_id, tanda_id, usuarios(nombre, telefono)'),
      ]);
      if (tandasRes.error) {
        console.error('Error cargando tandas:', tandasRes.error);
        setErrorCarga('Error al cargar tandas: ' + tandasRes.error.message);
      }
      // Unir participantes a tandas
      const tandasConParts = (tandasRes.data || []).map(tanda => ({
        ...tanda,
        participantes: (partsRes.data || []).filter(p => p.tanda_id === tanda.id),
      }));
      setTandas(tandasConParts);
      // Unir pagos con participantes y tandas
      const pagosConParts = (pagosRes.data || []).map(pago => {
        const part = (partsRes.data || []).find(pt => pt.id === pago.participante_id);
        const tanda = (tandasRes.data || []).find(td => td.id === part?.tanda_id);
        return { ...pago, participantes: part ? { ...part, tandas: tanda ? { nombre: tanda.nombre, monto: tanda.monto, semana_actual: tanda.semana_actual } : null } : null };
      });
      setPendientes(pagosConParts);
    } catch (err) {
      console.error('Error en cargar:', err);
      setErrorCarga('Error de conexión: ' + err.message);
    }
    setLoading(false);
  };

  const confirmarPago = async (pagoId, participanteId, semana) => {
    const { error } = await supabase.from('pagos').update({ pagado: true, fecha_pago: new Date().toISOString() }).eq('id', pagoId);
    if (error) { showToast('Error: ' + error.message, 'error'); return; }
    await audit('pago_confirmado', { pago_id: pagoId, participante_id: participanteId, semana, confirmado_por: usuario?.id, admin_nombre: usuario?.nombre });
    showToast('✅ Pago confirmado', 'success');
    cargar();
  };

  const MOTIVOS = [
    'Imagen no legible o borrosa',
    'Monto incorrecto',
    'Fecha incorrecta — comprobante no es de esta semana',
    'Cuenta de destino incorrecta',
    'Comprobante duplicado — ya fue registrado antes',
    'No corresponde a una transferencia bancaria',
    'Nombre del titular no coincide',
    'Comprobante inválido o alterado',
  ];
  const [modalRechazo, setModalRechazo] = useState(null); // pagoId al abrir
  const [motivoSeleccionado, setMotivoSeleccionado] = useState('');
  const [editandoClabe, setEditandoClabe] = useState(null); // tanda id
  const [nuevaClabe, setNuevaClabe] = useState('');
  const [mostrarCrear, setMostrarCrear] = useState(false);
  const [nuevaTanda, setNuevaTanda] = useState({ nombre: '', monto: '', semanas: '', clabe: '', codigo: '' });
  const [creando, setCreando] = useState(false);

  const crearTanda = async () => {
    const { nombre, monto, semanas, clabe } = nuevaTanda;
    if (!nombre.trim()) { showToast('Ingresa el nombre de la tanda', 'error'); return; }
    if (!monto || Number(monto) <= 0) { showToast('Ingresa un monto válido', 'error'); return; }
    if (!semanas || Number(semanas) < 2) { showToast('Mínimo 2 semanas', 'error'); return; }
    if (clabe) {
      if (!/^\d{18}$/.test(clabe)) { showToast('La CLABE debe tener 18 dígitos', 'error'); return; }
      const pesos = [3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7];
      let suma = 0;
      for (let i = 0; i < 17; i++) suma += (parseInt(clabe[i]) * pesos[i]) % 10;
      if (parseInt(clabe[17]) !== (10 - (suma % 10)) % 10) { showToast('CLABE inválida — verifica el número', 'error'); return; }
    }
    setCreando(true);
    const { error } = await supabase.from('tandas').insert({
      nombre: nombre.trim(),
      monto: Number(monto),
      semanas: Number(semanas),
      clabe: clabe || null,
      semana_actual: 1,
      estado: 'pendiente',
    });
    setCreando(false);
    if (error) { showToast('Error: ' + error.message, 'error'); return; }
    showToast('Tanda creada', 'success');
    setNuevaTanda({ nombre: '', monto: '', semanas: '', clabe: '', codigo: '' });
    setMostrarCrear(false);
    cargar();
  };

  const abrirRechazo = (pagoId) => {
    setModalRechazo(pagoId);
    setMotivoSeleccionado('');
  };

  const confirmarRechazo = async () => {
    if (!motivoSeleccionado) { showToast('Selecciona un motivo', 'error'); return; }
    const { error } = await supabase.from('pagos').update({
      comprobante_url: null,
      motivo_rechazo: motivoSeleccionado,
      estado_comprobante: 'rechazado',
    }).eq('id', modalRechazo);
    if (error) { showToast('Error: ' + error.message, 'error'); return; }
    await audit('pago_rechazado', { pago_id: modalRechazo, motivo: motivoSeleccionado });
    setModalRechazo(null);
    showToast('Comprobante rechazado', 'info');
    cargar();
  };

  return (
    <div style={{ minHeight: '100vh', background: C.gray50, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <Toast msg={toast.msg} type={toast.type} />

      {/* Modal selección motivo rechazo */}
      {modalRechazo && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 700, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: '28px 20px 40px', width: '100%', maxWidth: 480 }}>
            <div style={{ fontSize: 18, fontWeight: 900, color: '#1E293B', marginBottom: 6 }}>¿Por qué rechazas este comprobante?</div>
            <div style={{ fontSize: 13, color: '#64748B', marginBottom: 20 }}>El usuario verá este motivo en su app para corregirlo.</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
              {MOTIVOS.map(m => (
                <button key={m} onClick={() => setMotivoSeleccionado(m)} style={{
                  background: motivoSeleccionado === m ? '#EFF6FF' : '#F8FAFC',
                  border: `2px solid ${motivoSeleccionado === m ? '#1A56DB' : '#E5E7EB'}`,
                  borderRadius: 12, padding: '13px 16px', textAlign: 'left', cursor: 'pointer',
                  fontSize: 14, fontWeight: motivoSeleccionado === m ? 800 : 600,
                  color: motivoSeleccionado === m ? '#1A56DB' : '#374151',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <span style={{ fontSize: 18 }}>{motivoSeleccionado === m ? '🔵' : '⚪'}</span>
                  {m}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn onClick={() => setModalRechazo(null)} variant="outline" style={{ flex: 1, padding: '13px 0' }}>Cancelar</Btn>
              <Btn onClick={confirmarRechazo} variant="danger" style={{ flex: 1, padding: '13px 0' }}>Confirmar rechazo</Btn>
            </div>
          </div>
        </div>
      )}

      <NavbarApp onMenuOpen={onMenu} titulo="Panel Admin" onHome={onHome} />

      {/* Botón dashboard completo */}
      <div style={{ padding: '12px 16px 0' }}>
        <button onClick={() => window.open('https://tandaclick.com?panel=admin', '_blank')}
          style={{ width: '100%', padding: '14px 0', borderRadius: 12, border: `1.5px solid ${C.primary}`, background: C.primaryLt, color: C.primary, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          🖥 Abrir Dashboard completo
        </button>
      </div>

      {/* Tabs */}
      <div style={{ background: C.white, borderBottom: `1px solid ${C.gray200}`, display: 'flex', position: 'sticky', top: 64, zIndex: 90 }}>
        {[
          { id: 'pendientes', label: `Comprobantes${pendientes.length > 0 ? ` (${pendientes.length})` : ''}`, icon: '📋' },
          { id: 'tandas', label: 'Tandas', icon: '🤝' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setVistaAdmin(tab.id)} style={{
            flex: 1, padding: '14px 8px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif",
            color: vistaAdmin === tab.id ? C.primary : C.textLt,
            borderBottom: vistaAdmin === tab.id ? `3px solid ${C.primary}` : '3px solid transparent',
          }}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      <div style={{ padding: '16px 16px 80px' }}>
        {loading && <div style={{ textAlign: 'center', padding: 40, color: C.textLt }}>Cargando...</div>}

        {/* ── COMPROBANTES PENDIENTES ── */}
        {!loading && vistaAdmin === 'pendientes' && (
          <>
            {pendientes.length === 0 ? (
              <Card style={{ textAlign: 'center', padding: 40 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 6 }}>Todo al día</div>
                <div style={{ fontSize: 13, color: C.textMd }}>No hay comprobantes pendientes de revisar.</div>
              </Card>
            ) : (
              pendientes.map(pago => {
                const p = pago.participantes;
                const esImagen = getComprobanteUrl(pago.comprobante_url)?.match(/\.(jpg|jpeg|png|webp|heic)$/i);
                return (
                  <Card key={pago.id} style={{ marginBottom: 16 }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 900, color: C.text }}>{p?.usuarios?.nombre}</div>
                        <div style={{ fontSize: 12, color: C.textMd, marginTop: 2 }}>{p?.tandas?.nombre}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 15, fontWeight: 900, color: C.primary }}>{fmt(p?.tandas?.monto)}</div>
                        <div style={{ fontSize: 11, color: C.textLt, marginTop: 2 }}>Semana {pago.semana} · Turno #{p?.turno}</div>
                      </div>
                    </div>

                    {/* Comprobante */}
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: C.textLt, marginBottom: 8 }}>COMPROBANTE</div>
                      {esImagen ? (
                        <div style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${C.gray200}`, cursor: 'pointer' }}
                          onClick={() => window.open(getComprobanteUrl(pago.comprobante_url), '_blank')}>
                          <img src={getComprobanteUrl(pago.comprobante_url)} alt="Comprobante" style={{ width: '100%', maxHeight: 220, objectFit: 'cover', display: 'block' }} />
                          <div style={{ padding: '8px 12px', background: C.gray50, fontSize: 11, color: C.textLt, textAlign: 'center' }}>
                            Toca para ver completo 🔍
                          </div>
                        </div>
                      ) : (
                        <a href={getComprobanteUrl(pago.comprobante_url)} target="_blank" rel="noreferrer"
                          style={{ display: 'flex', gap: 10, alignItems: 'center', background: C.gray100, borderRadius: 10, padding: '12px 14px', textDecoration: 'none' }}>
                          <span style={{ fontSize: 24 }}>📄</span>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: C.primary }}>Ver comprobante PDF</div>
                            <div style={{ fontSize: 11, color: C.textLt }}>Abre en nueva pestaña</div>
                          </div>
                        </a>
                      )}
                    </div>

                    {/* Datos del usuario */}
                    <div style={{ background: C.gray50, borderRadius: 10, padding: '10px 12px', marginBottom: 14, fontSize: 12, color: C.textMd }}>
                      📱 {p?.usuarios?.telefono}
                    </div>

                    {/* Botones */}
                    <div style={{ display: 'flex', gap: 10 }}>
                      <Btn onClick={() => confirmarPago(pago.id, p?.id, pago.semana)}
                        style={{ background: C.success, border: `1px solid ${C.success}`, flex: 1, padding: '13px 0', fontSize: 14 }}>
                        ✅ Confirmar pago
                      </Btn>
                      <Btn onClick={() => abrirRechazo(pago.id)} variant="danger"
                        style={{ flex: 1, padding: '13px 0', fontSize: 14 }}>
                        ✕ Rechazar
                      </Btn>
                    </div>
                  </Card>
                );
              })
            )}
          </>
        )}

        {/* ── TANDAS ── */}
        {!loading && vistaAdmin === 'tandas' && (
          <>
            {/* Botón crear tanda */}
            {!mostrarCrear && (
              <Btn onClick={() => setMostrarCrear(true)} style={{ marginBottom: 16, padding: '13px 0', fontSize: 14, width: '100%' }}>
                + Crear nueva tanda
              </Btn>
            )}

            {/* Formulario crear tanda */}
            {mostrarCrear && (
              <Card style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 16, fontWeight: 900, color: C.text, marginBottom: 16 }}>Nueva tanda</div>
                {[
                  { label: 'Nombre', key: 'nombre', placeholder: 'Ej: Tanda Familiar', type: 'text' },
                  { label: 'Monto semanal ($)', key: 'monto', placeholder: '200', type: 'number' },
                  { label: 'Semanas', key: 'semanas', placeholder: '10', type: 'number' },
                  { label: 'CLABE (18 dígitos)', key: 'clabe', placeholder: '728969000170148422', type: 'tel', maxLength: 18 },
                ].map(field => (
                  <div key={field.key} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 4 }}>{field.label}</div>
                    <input
                      type={field.type} placeholder={field.placeholder} maxLength={field.maxLength}
                      value={nuevaTanda[field.key]}
                      onChange={e => {
                        let val = e.target.value;
                        if (field.key === 'clabe') val = val.replace(/\D/g, '').slice(0, 18);
                        setNuevaTanda(prev => ({ ...prev, [field.key]: val }));
                      }}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, fontSize: 14, fontFamily: "'Plus Jakarta Sans', sans-serif", outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                ))}
                {nuevaTanda.monto && nuevaTanda.semanas && (
                  <div style={{ background: C.primaryLt, borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13, fontWeight: 700, color: C.primary }}>
                    Tanda completa: {fmt(Number(nuevaTanda.monto) * Number(nuevaTanda.semanas))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                  <Btn onClick={() => { setMostrarCrear(false); setNuevaTanda({ nombre: '', monto: '', semanas: '', clabe: '', codigo: '' }); }} variant="outline" style={{ flex: 1, padding: '12px 0' }}>Cancelar</Btn>
                  <Btn onClick={crearTanda} disabled={creando} style={{ flex: 1, padding: '12px 0' }}>{creando ? 'Creando...' : 'Crear tanda'}</Btn>
                </div>
              </Card>
            )}

            {errorCarga && (
              <Card style={{ textAlign: 'center', padding: 24, marginBottom: 16, background: '#FEF2F2' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#DC2626', marginBottom: 8 }}>{errorCarga}</div>
                <Btn onClick={cargar} variant="outline" style={{ padding: '10px 24px' }}>Reintentar</Btn>
              </Card>
            )}
            {!errorCarga && tandas.length === 0 && (
              <Card style={{ textAlign: 'center', padding: 40 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🤝</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 6 }}>Sin tandas</div>
                <div style={{ fontSize: 13, color: C.textMd }}>No hay tandas creadas aún.</div>
              </Card>
            )}
            {tandas.map(tanda => {
              const participantes = tanda.participantes || [];
              const conTurno = participantes.filter(p => p.turno).length;
              return (
                <Card key={tanda.id} style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 900, color: C.text }}>{tanda.nombre}</div>
                      <div style={{ fontSize: 13, color: C.textMd, marginTop: 2 }}>{fmt(tanda.monto)}/semana · {tanda.semanas} semanas</div>
                    </div>
                    <span style={{ background: C.successLt, color: C.success, fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 99 }}>{tanda.estado}</span>
                  </div>

                  {/* CLABE */}
                  <div style={{ background: C.gray50, borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: C.textLt }}>CLABE</div>
                        {editandoClabe === tanda.id ? (
                          <input type="tel" value={nuevaClabe} onChange={e => setNuevaClabe(e.target.value.replace(/\D/g, '').slice(0, 18))}
                            placeholder="18 dígitos" maxLength={18}
                            style={{ fontSize: 14, fontFamily: 'monospace', fontWeight: 700, color: C.text, border: `2px solid ${C.primary}`, borderRadius: 8, padding: '6px 10px', width: '100%', maxWidth: 220, marginTop: 4, outline: 'none' }} />
                        ) : (
                          <div style={{ fontSize: 14, fontFamily: 'monospace', fontWeight: 700, color: C.text, marginTop: 2 }}>{tanda.clabe || 'Sin CLABE'}</div>
                        )}
                      </div>
                      {editandoClabe === tanda.id ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={async () => {
                            if (!/^\d{18}$/.test(nuevaClabe)) { showToast('La CLABE debe tener 18 dígitos', 'error'); return; }
                            const { error } = await supabase.from('tandas').update({ clabe: nuevaClabe }).eq('id', tanda.id);
                            if (error) { showToast('Error: ' + error.message, 'error'); return; }
                            showToast('CLABE actualizada', 'success');
                            setEditandoClabe(null);
                            cargar();
                          }} style={{ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 8, border: 'none', background: C.primary, color: '#fff', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Guardar</button>
                          <button onClick={() => setEditandoClabe(null)} style={{ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 8, border: `1px solid ${C.gray200}`, background: '#fff', color: C.textMd, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Cancelar</button>
                        </div>
                      ) : (
                        <button onClick={() => { setEditandoClabe(tanda.id); setNuevaClabe(tanda.clabe || ''); }}
                          style={{ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 8, border: `1px solid ${C.primaryMd}`, background: C.primaryLt, color: C.primary, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Editar</button>
                      )}
                    </div>
                  </div>

                  {/* Progreso */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.textLt, marginBottom: 6 }}>
                    <span>Semana {tanda.semana_actual} de {tanda.semanas}</span>
                    <span>{conTurno}/{tanda.semanas} participantes</span>
                  </div>
                  <div style={{ background: C.gray200, borderRadius: 99, height: 6, overflow: 'hidden', marginBottom: 14 }}>
                    <div style={{ width: `${(tanda.semana_actual / tanda.semanas) * 100}%`, height: '100%', background: C.primary, borderRadius: 99 }} />
                  </div>

                  {/* Lista participantes */}
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.textLt, marginBottom: 8 }}>PARTICIPANTES</div>
                  {participantes.sort((a, b) => (a.turno || 99) - (b.turno || 99)).map((p, i) => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < participantes.length - 1 ? `1px solid ${C.gray100}` : 'none' }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: p.turno === tanda.semana_actual ? C.primary : C.gray100, color: p.turno === tanda.semana_actual ? C.white : C.textLt, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 900, flexShrink: 0 }}>
                        {p.turno || '?'}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, flex: 1 }}>{p.usuarios?.nombre || 'Participante'}</div>
                      {p.turno === tanda.semana_actual && (
                        <span style={{ background: C.successLt, color: C.success, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99 }}>Cobra hoy</span>
                      )}
                    </div>
                  ))}

                  {/* Avanzar semana */}
                  <div style={{ marginTop: 14 }}>
                    <Btn variant="outline" onClick={async () => {
                      if (tanda.semana_actual >= tanda.semanas) { showToast('Esta tanda ya terminó', 'info'); return; }
                      const { error } = await supabase.from('tandas').update({ semana_actual: tanda.semana_actual + 1 }).eq('id', tanda.id);
                      if (error) { showToast('Error: ' + error.message, 'error'); return; }
                      await audit('semana_avanzada', { tanda_id: tanda.id, semana_nueva: tanda.semana_actual + 1 });
                      showToast(`Semana avanzada a ${tanda.semana_actual + 1} ✓`, 'success');
                      cargar();
                    }} style={{ padding: '11px 0', fontSize: 13 }}>
                      ▶ Avanzar a semana {tanda.semana_actual + 1}
                    </Btn>
                  </div>
                </Card>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

export { PanelAdmin };
